import { FormControl, InputLabel } from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import IntakeCard from 'candidate/components/IntakeCard'
import { QuestionWrapper } from 'candidate/components/QuestionWrapper'
import { ChoiceBucket } from 'candidate/models/intake'
import { useEffect, useState } from 'react'
import NextButton from 'shared/components/NextButton'

import type { Dispatch, FC, SetStateAction } from 'react'

export interface CountryHometownAnswer {
    country: ChoiceBucket | undefined
    province: ChoiceBucket | undefined
    state: ChoiceBucket | undefined
    city: string
}
interface Props {
    answer?: CountryHometownAnswer
    callback: Dispatch<any>
    countryChoices: ChoiceBucket[]
    provinceChoices: ChoiceBucket[]
    stateChoices: ChoiceBucket[]
    prompt: string
    skipCallback?: Dispatch<SetStateAction<string>>
    skipText?: string
}

const CountryHometownQuestion: FC<Props> = ({
    answer,
    callback,
    countryChoices,
    provinceChoices,
    stateChoices,
    prompt,
    skipCallback,
    skipText,
}: Props) => {
    const [response, setResponse] = useState<CountryHometownAnswer>({
        country: answer?.country || undefined,
        province: answer?.province || undefined,
        state: answer?.state || undefined,
        city: answer?.city || '',
    })

    useEffect(() => {
        if (answer) {
            setResponse(answer)
        }
    }, [answer, prompt])

    const submitData = () => {
        callback({ response })
    }

    return (
        <IntakeCard
            question={prompt}
            skipCallback={skipCallback}
            skipText={skipText}
        >
            <QuestionWrapper>
                <FormControl fullWidth>
                    <InputLabel id="country_label">Country</InputLabel>
                    <Select
                        fullWidth
                        id="country"
                        labelId="country_label"
                        label="Country"
                        value={response?.country?.choice || ''}
                        onChange={(e) => {
                            const country = countryChoices.find(
                                (choice: ChoiceBucket) =>
                                    choice.choice === e.target.value
                            )
                            setResponse({ ...response, country })
                        }}
                    >
                        {countryChoices.map((choice: ChoiceBucket) => (
                            <MenuItem key={choice.choice} value={choice.choice}>
                                {choice.choice}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {response?.country?.choice === 'Canada' && (
                    <FormControl fullWidth>
                        <InputLabel id="province_label">Province</InputLabel>
                        <Select
                            id="province"
                            labelId="province_label"
                            label="Province"
                            value={response?.province?.choice || ''}
                            onChange={(e) => {
                                const province = provinceChoices.find(
                                    (choice: ChoiceBucket) =>
                                        choice.choice === e.target.value
                                )
                                setResponse({ ...response, province })
                            }}
                        >
                            {provinceChoices.map((choice: ChoiceBucket) => (
                                <MenuItem
                                    key={choice.choice}
                                    value={choice.choice}
                                >
                                    {choice.choice}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {response?.country?.choice === 'United States' && (
                    <FormControl fullWidth>
                        <InputLabel id="state_label">State</InputLabel>
                        <Select
                            id="state"
                            labelId="state_label"
                            label="State"
                            value={response?.state?.choice || ''}
                            onChange={(e) => {
                                const state = stateChoices.find(
                                    (choice: ChoiceBucket) =>
                                        choice.choice === e.target.value
                                )
                                setResponse({ ...response, state })
                            }}
                        >
                            {stateChoices.map((choice: ChoiceBucket) => (
                                <MenuItem
                                    key={choice.choice}
                                    value={choice.choice}
                                >
                                    {choice.choice}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {response?.country?.choice && (
                    <TextField
                        id="city"
                        label="City"
                        placeholder="Enter your city"
                        value={response?.city}
                        fullWidth
                        onChange={(e) =>
                            setResponse({
                                ...response,
                                city: e.target.value,
                            })
                        }
                    />
                )}
            </QuestionWrapper>

            <NextButton
                disabled={
                    ((response?.country?.choice === 'United States' &&
                        response?.state) ||
                        (response?.country?.choice === 'Canada' &&
                            response.province) ||
                        response?.country?.choice) &&
                    response.city
                        ? false
                        : true
                }
                onClickHandler={submitData}
            />
        </IntakeCard>
    )
}

export default CountryHometownQuestion
